// @givmo/org-console — the AUTOMATIC CONNECTION setting's wire (Givmo MCP MCP-P17b, D-MCP-106).
//
// `GET`/`PUT`/`DELETE /org/{org_ref}/automatic-connection` (backend MCP-P17a): the organization's ONE
// trusted issuer for Enterprise-Managed Authorization — Claude's, or the organization's identity
// provider's — whose signed assertions connect its people's AI clients to the organization connector
// without the consent screen. A Global Admin's only, by the authority that ends a connected AI.
//
// ══ THE PAGE RENDERS FROM THE SERVER'S RECORD (plan §2.4 item 1) ══════════════════════════════════
//
// A read naming a candidate issuer (`?issuer=…&uses_tenant_id=…`) answers what Givmo found when it
// checked that address just now (`proposed_change`) and the `content_hash` a confirm of exactly that
// change must send back. The page shows the SERVER's answer, never the typed address dressed up as
// one, and the confirm carries the hash. A confirm whose hash no longer matches what the server would
// render (another Global Admin changed it, the issuer's discovery document moved, or the change can no
// longer be made) is the 409 `automatic_connection_changed`, with the fresh render and its hash in the
// refusal's `context` (`automaticConnectionChangedTo`).
//
// ⚠ ABSENT IS NOT AN ERROR. The three routes answer the console's route miss (404) while the backend's
// `MCP_EMA_ENABLED` is off, so a 404 here means "this environment has no automatic connection" and the
// section renders nothing (`isOrgConsoleUnavailable`, status alone, as everywhere on `/org`).
//
// ⚠ HAND-WRITTEN TYPES, by the transport's own disposition (`org-console-transport.ts`, the api-client
// doc's § "A whole ROUTE the snapshot lacks"): the committed OpenAPI snapshot predates the routes.
// Contract read from the backend branch `feat/ema-automatic-connection-setting`
// (`app/domain/entities/org_automatic_connection_entities.py`).

import { firstErrorCode, firstErrorContext, givmoClient, isGivmoError } from '@givmo/api-client';

/** The identity source an issuer is. */
export type AutomaticConnectionKind = 'claude' | 'identity_provider';

/** Why an issuer cannot be trusted, as the server names it (a closed set; the words are this package's). */
export type AutomaticConnectionProblem =
  | 'issuer_invalid'
  | 'issuer_unreachable'
  | 'issuer_mismatch'
  | 'issuer_no_keys'
  | 'issuer_held_elsewhere';

const PROBLEMS: readonly AutomaticConnectionProblem[] = [
  'issuer_invalid',
  'issuer_unreachable',
  'issuer_mismatch',
  'issuer_no_keys',
  'issuer_held_elsewhere',
];

/** The stored trusted issuer: automatic connection is on. */
export interface AutomaticConnectionIssuer {
  issuer: string;
  kind: AutomaticConnectionKind;
  usesTenantId: boolean;
  setByName: string;
  setAt: string;
}

/** The change a candidate issuer would make, as the server checked it. */
export interface AutomaticConnectionProposal {
  issuer: string;
  kind: AutomaticConnectionKind | null;
  usesTenantId: boolean;
  keysHost: string | null;
  /** `null` when the change can be confirmed. An unknown reason reads as `issuer_unreachable`. */
  problem: AutomaticConnectionProblem | null;
}

/** The fresh sign-in a confirm will ask for: a hint so the page can ask first, never the decision. */
export interface AutomaticConnectionStepUp {
  action: string;
  windowSeconds: number;
  satisfied: boolean;
}

/** The setting, as its Global Admins see it. */
export interface AutomaticConnection {
  on: boolean;
  /** Givmo's id for the organization: what goes in Claude's "Tenant ID (optional)" field. */
  organizationId: string;
  trustedIssuer: AutomaticConnectionIssuer | null;
  proposedChange: AutomaticConnectionProposal | null;
  contentHash: string | null;
  stepUp: AutomaticConnectionStepUp | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asKind(value: unknown): AutomaticConnectionKind | null {
  return value === 'claude' || value === 'identity_provider' ? value : null;
}

function decodeIssuer(raw: unknown): AutomaticConnectionIssuer | null {
  const row = asRecord(raw);
  const issuer = asString(row?.issuer);
  const kind = asKind(row?.kind);
  if (row === null || issuer === null || kind === null) return null;
  const setBy = asRecord(row.set_by);
  return {
    issuer,
    kind,
    usesTenantId: row.uses_tenant_id === true,
    setByName: asString(setBy?.name) ?? '',
    setAt: asString(row.set_at) ?? '',
  };
}

function decodeProposal(raw: unknown): AutomaticConnectionProposal | null {
  const row = asRecord(raw);
  const issuer = typeof row?.issuer === 'string' ? row.issuer : null;
  if (row === null || issuer === null) return null;
  const rawProblem = row.problem;
  let problem: AutomaticConnectionProblem | null = null;
  if (typeof rawProblem === 'string') {
    // A reason this build does not know is still a refusal: never read it as confirmable.
    problem = PROBLEMS.find((known) => known === rawProblem) ?? 'issuer_unreachable';
  }
  return {
    issuer,
    kind: asKind(row.kind),
    usesTenantId: row.uses_tenant_id === true,
    keysHost: asString(row.keys_host),
    problem,
  };
}

function decodeStepUp(raw: unknown): AutomaticConnectionStepUp | null {
  const row = asRecord(raw);
  const action = asString(row?.action);
  if (row === null || action === null) return null;
  return {
    action,
    windowSeconds: typeof row.window_seconds === 'number' ? row.window_seconds : 0,
    satisfied: row.satisfied === true,
  };
}

/**
 * The setting from a read's (or a write's) answer. Tolerant: `null` for anything that is not the
 * setting's shape. A proposal with a problem never carries a hash a confirm could send.
 */
export function decodeAutomaticConnection(raw: unknown): AutomaticConnection | null {
  const body = asRecord(raw);
  const organizationId = asString(body?.organization_id);
  if (body === null || organizationId === null) return null;
  const proposedChange = decodeProposal(body.proposed_change);
  const contentHash = asString(body.content_hash);
  return {
    on: body.status === 'on',
    organizationId,
    trustedIssuer: decodeIssuer(body.trusted_issuer),
    proposedChange,
    contentHash: proposedChange !== null && proposedChange.problem === null ? contentHash : null,
    stepUp: decodeStepUp(body.step_up),
  };
}

/** `/org/{org_ref}/automatic-connection`. */
function settingUrl(orgRef: string): string {
  return `/org/${encodeURIComponent(orgRef)}/automatic-connection`;
}

/** What a read asks: the stored setting alone, or that plus a candidate issuer's check. */
export interface AutomaticConnectionRead {
  issuer?: string;
  usesTenantId?: boolean;
  signal?: AbortSignal;
}

/** Read the setting; with `issuer`, also the change that issuer would make. Writes nothing. */
export async function fetchAutomaticConnection(
  orgRef: string,
  read: AutomaticConnectionRead = {},
): Promise<unknown> {
  const query =
    read.issuer === undefined
      ? undefined
      : { issuer: read.issuer, uses_tenant_id: read.usesTenantId === true ? 'true' : 'false' };
  const { data } = await givmoClient.get({
    url: settingUrl(orgRef),
    ...(query === undefined ? {} : { query }),
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

/** The confirm's body: exactly the change the page showed, and the hash it was shown with. */
export interface SetAutomaticConnectionBody {
  issuer: string;
  uses_tenant_id: boolean;
  content_hash: string;
}

/** Turn automatic connection on, or change its issuer: needs a fresh sign-in (`@givmo/step-up`). */
export async function setAutomaticConnection(
  orgRef: string,
  body: SetAutomaticConnectionBody,
): Promise<unknown> {
  const { data } = await givmoClient.put({ url: settingUrl(orgRef), body, throwOnError: true });
  return data;
}

/** Turn automatic connection off. Idempotent; asks for no fresh sign-in. */
export async function turnOffAutomaticConnection(orgRef: string): Promise<unknown> {
  const { data } = await givmoClient.delete({ url: settingUrl(orgRef), throwOnError: true });
  return data;
}

/**
 * The fresh render a drifted confirm was refused with (409 `automatic_connection_changed`), or `null`
 * for any other failure.
 */
export function automaticConnectionChangedTo(error: unknown): AutomaticConnection | null {
  if (!isGivmoError(error) || error.status !== 409) return null;
  if (firstErrorCode(error) !== 'automatic_connection_changed') return null;
  return decodeAutomaticConnection(firstErrorContext(error)?.render);
}
